import type {
  CheckinStatus,
  EditScope,
  ExceptionInput,
  Item,
  ItemInput,
  ItemPatch,
  LocalDate,
  Occurrence,
} from '@planer/shared';
import { addDays } from '@planer/shared';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api, NetworkError, type Schedule } from './api';
import { queryKeys } from './hooks';
import { enqueueCheckin, flushOutbox } from './outbox';
import { closeNotificationsFor } from './push';
import { showToast } from './toast';

export function haptic(pattern: number | number[] = 10): void {
  // Android only; iOS PWAs have no vibration API.
  if ('vibrate' in navigator) navigator.vibrate(pattern);
}

const STATUS_TEXT: Record<CheckinStatus, string> = {
  done: 'opravljeno',
  missed: 'ni opravljeno',
  skipped: 'preskočeno',
};

type CheckTarget = Pick<Occurrence, 'itemId' | 'originDate' | 'source'>;

function write(occ: CheckTarget, status: CheckinStatus | null) {
  // Answered here: the reminder / "Si opravil?" notification for it is no longer needed.
  if (status) void closeNotificationsFor(occ.itemId, occ.originDate).catch(() => undefined);
  void enqueueCheckin({
    target: occ.source === 'event' ? 'event' : 'item',
    itemId: occ.itemId,
    // Check-ins belong to the day the rule produced the occurrence on (also after a move).
    date: occ.originDate,
    status,
    reason: null,
    clientTs: new Date().toISOString(),
  });
}

/** Records a check-in (works offline) and offers undo. */
export function setCheckin(
  occ: CheckTarget & Pick<Occurrence, 'title'>,
  status: CheckinStatus | null,
  previous: CheckinStatus | null | undefined,
): void {
  if (status === (previous ?? null)) return;
  haptic(status === 'done' ? 12 : [8, 40, 8]);
  write(occ, status);
  showToast(
    {
      message: status ? `${occ.title}: ${STATUS_TEXT[status]}` : `${occ.title}: oznaka odstranjena`,
      tone: status ?? 'neutral',
      actionLabel: 'Razveljavi',
      onAction: () => {
        write(occ, previous ?? null);
      },
    },
    3500,
  );
}

export function errorMessage(err: unknown): string {
  if (err instanceof NetworkError) return 'Ni povezave. Spremembe urnika potrebujejo internet.';
  return 'Shranjevanje ni uspelo. Poskusi znova.';
}

export function useScheduleActions() {
  const qc = useQueryClient();
  const refresh = () => qc.invalidateQueries({ queryKey: queryKeys.schedule });
  const onError = (err: unknown) => {
    showToast({ message: errorMessage(err), tone: 'missed' });
  };
  const schedule = () => qc.getQueryData<Schedule>(queryKeys.schedule);
  const setSchedule = (fn: (s: Schedule) => Schedule) => {
    qc.setQueryData<Schedule>(queryKeys.schedule, (s) => (s ? fn(s) : s));
  };

  const create = useMutation({
    mutationFn: (input: ItemInput) => api.createItem(input),
    onSuccess: refresh,
    onError,
  });

  const update = useMutation({
    mutationFn: async ({ id, patch }: { id: string; patch: ItemPatch }) => {
      // Deliver queued check-ins first, so a series split moves them along.
      await flushOutbox();
      return api.updateItem(id, patch);
    },
    onSuccess: refresh,
    onError,
  });

  const exception = useMutation({
    mutationFn: ({ id, date, input }: { id: string; date: LocalDate; input: ExceptionInput }) =>
      api.putException(id, date, input),
    onSuccess: refresh,
    onError,
  });

  /** "Samo ta": skip one occurrence; undo restores what was there before (e.g. a move). */
  function skipOne(occ: Occurrence) {
    const before = schedule()?.exceptions.find(
      (e) => e.itemId === occ.itemId && e.date === occ.originDate,
    );
    exception.mutate(
      { id: occ.itemId, date: occ.originDate, input: { action: 'skip' } },
      {
        onSuccess: () => {
          showToast({
            message: `${occ.title} preskočeno za ta dan`,
            actionLabel: 'Razveljavi',
            onAction: () => {
              const undo = before
                ? api.putException(occ.itemId, occ.originDate, {
                    action: 'move',
                    newTime: before.newTime ?? occ.startTime,
                    newDurationMin: before.newDurationMin,
                    newDate: before.newDate,
                  })
                : api.deleteException(occ.itemId, occ.originDate);
              void undo.then(refresh, onError);
            },
          });
        },
      },
    );
  }

  /** Deletes right away (the server keeps history) and offers undo. */
  async function remove(occ: Occurrence, scope: EditScope) {
    const item = schedule()?.items.find((i) => i.id === occ.itemId);
    if (!item) return;
    const previousEndDate = item.endDate;
    const before = schedule();
    const repeats = item.rrule !== null || item.templateId !== null;
    const following = scope === 'following' && repeats && occ.originDate > item.startDate;
    setSchedule((s) => ({
      ...s,
      items: following
        ? s.items.map((i): Item =>
            i.id === item.id ? { ...i, endDate: addDays(occ.originDate, -1) } : i,
          )
        : s.items.filter((i) => i.id !== item.id),
    }));
    try {
      await flushOutbox();
      await api.deleteItem(
        item.id,
        following ? 'following' : 'all',
        following ? occ.originDate : undefined,
      );
    } catch (err) {
      // Not deleted on the server: show it again (a refetch alone fails when offline).
      if (before) qc.setQueryData<Schedule>(queryKeys.schedule, before);
      void refresh();
      onError(err);
      return;
    }
    void refresh();
    showToast({
      message: following ? `${item.title} izbrisano od tega dne` : `${item.title} izbrisano`,
      actionLabel: 'Razveljavi',
      onAction: () => {
        const undo = following
          ? api.updateItem(item.id, { scope: 'all', changes: { endDate: previousEndDate } })
          : api.restoreItem(item.id);
        void undo.then(refresh, onError);
      },
    });
  }

  /** The occurrence as it was, for undoing a move. */
  function snapshot(occ: Occurrence) {
    const s = schedule();
    return {
      item: s?.items.find((i) => i.id === occ.itemId),
      exception: s?.exceptions.find((e) => e.itemId === occ.itemId && e.date === occ.originDate),
    };
  }

  return { create, update, exception, skipOne, remove, snapshot, refresh };
}
